import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChevronLeft, Lock, RefreshCw, Unlock } from 'lucide-react';
import { LopButton } from '../../components/LopButton';
import { useTheme } from '../../contexts/ThemeContext';
import { t } from '../../i18n/t';
import { getDashboard } from '../../api/dashboard';
import type { DashboardData } from '../../types/Dashboard';
import {
  FUND_TYPE_ORDER,
  formatClock,
  formatCount,
  formatDate,
  formatDateTime,
  formatDayMonth,
  formatPercent,
  formatShekelsFull,
  formatShekelsShort,
  fundTypeViz,
} from './dashboardFormat';
import './DashboardPage.css';
import { showErrorToast, describeError } from '../../components/ErrorToast';

const REFRESH_MS = 5 * 60 * 1000;

// ───── shared building blocks ─────

function Card({
  title,
  headerExtra,
  to,
  className,
  children,
}: {
  title: string;
  headerExtra?: React.ReactNode;
  to?: string;
  className?: string;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const clickable = Boolean(to);
  return (
    <section
      className={`dash-card${clickable ? ' dash-card--link' : ''}${className ? ` ${className}` : ''}`}
      {...(clickable
        ? {
            role: 'link',
            tabIndex: 0,
            onClick: () => navigate(to as string),
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(to as string);
              }
            },
          }
        : {})}
    >
      <header className="dash-card__header">
        <h2 className="dash-card__title">{title}</h2>
        {headerExtra && <span className="dash-card__extra">{headerExtra}</span>}
        {clickable && <ChevronLeft className="dash-card__chevron" size={18} aria-hidden="true" />}
      </header>
      <div className="dash-card__body">{children}</div>
    </section>
  );
}

function EmptyState() {
  return (
    <div className="dash-empty" data-testid="dash-empty">
      <svg className="dash-empty__icon" width="44" height="44" viewBox="0 0 44 44" aria-hidden="true">
        <rect x="6" y="24" width="7" height="14" rx="2" />
        <rect x="18" y="16" width="7" height="22" rx="2" />
        <rect x="30" y="8" width="7" height="30" rx="2" />
      </svg>
      <span>{t('dashboard.noValuationData')}</span>
    </div>
  );
}

function ChartTooltip({
  active,
  title,
  rows,
}: {
  active?: boolean;
  title?: string;
  rows: { color?: string; label: string; value: string }[];
}) {
  if (!active || rows.length === 0) return null;
  return (
    <div className="dash-tooltip" dir="rtl">
      {title && <div className="dash-tooltip__title">{title}</div>}
      {rows.map((r) => (
        <div key={r.label} className="dash-tooltip__row">
          {r.color && <span className="dash-swatch" style={{ background: r.color }} />}
          <span className="dash-tooltip__label">{r.label}</span>
          <span className="dash-tooltip__value">{r.value}</span>
        </div>
      ))}
    </div>
  );
}

// ───── tiles ─────

function ValuationTile({ data }: { data: DashboardData }) {
  const { valuation } = data;
  const open = valuation.isDayOpen;
  const notSet = t('dashboard.dateNotSet');
  const last = formatDateTime(valuation.dayOpenLast);
  const state = open ? 'open' : 'closed';
  return (
    <Card
      title={t('dashboard.valuationTitle')}
      headerExtra={
        <span className={`dash-badge dash-badge--${state}`} data-testid="valuation-badge">
          {open ? <Unlock size={14} aria-hidden="true" /> : <Lock size={14} aria-hidden="true" />}
          {open ? t('dashboard.dayOpen') : t('dashboard.dayClosed')}
        </span>
      }
    >
      <ul className="dash-dates" data-state={state} data-testid="valuation-dates">
        <li className={`dash-date dash-date--${state}`}>
          <span className="dash-date__dot" aria-hidden="true" />
          {t('dashboard.israeliValuation', {
            date: formatDate(valuation.israeliValuationDate) ?? notSet,
          })}
        </li>
        <li className={`dash-date dash-date--${state}`}>
          <span className="dash-date__dot" aria-hidden="true" />
          {t('dashboard.foreignValuation', {
            date: formatDate(valuation.foreignValuationDate) ?? notSet,
          })}
        </li>
      </ul>
      {last && <div className="dash-muted">{t('dashboard.dayOpenLast', { value: last })}</div>}
    </Card>
  );
}

function AlertsTile({ data }: { data: DashboardData }) {
  const top = [...data.alerts.byType].sort((a, b) => b.count - a.count).slice(0, 3);
  return (
    <Card title={t('dashboard.alertsTitle')} to="/alerts">
      <div className="dash-hero" data-testid="alerts-total">
        {formatCount(data.alerts.total)}
      </div>
      {top.length === 0 ? (
        <div className="dash-muted">{t('dashboard.alertsEmpty')}</div>
      ) : (
        <ul className="dash-list">
          {top.map((a) => (
            <li key={a.alertTypeId} className="dash-list__row">
              <span className="dash-list__name">{a.alertTypeName}</span>
              <span className="dash-list__value">{formatCount(a.count)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ProgressBar({ label, done, total }: { label: string; done: number; total: number }) {
  const pct = total > 0 ? Math.min(100, (done / total) * 100) : 0;
  return (
    <div className="dash-progress">
      <div className="dash-progress__head">
        <span>{label}</span>
        <span className="dash-progress__val" dir={total > 0 ? 'ltr' : undefined}>
          {total > 0 ? `${formatCount(done)} / ${formatCount(total)}` : t('dashboard.readinessNone')}
        </span>
      </div>
      {total > 0 && (
        <div className="dash-progress__track" role="progressbar" aria-valuenow={done} aria-valuemin={0} aria-valuemax={total}>
          <div className="dash-progress__fill" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

function AgendaTile({ data }: { data: DashboardData }) {
  const { agenda } = data;
  const frac = agenda.total > 0 ? Math.min(1, agenda.done / agenda.total) : 0;
  const R = 38;
  const C = 2 * Math.PI * R;
  return (
    <Card title={t('dashboard.agendaTitle')} to="/agenda">
      <div className="dash-agenda">
        <div className="dash-ring" data-testid="agenda-ring">
          <svg viewBox="0 0 100 100" width="104" height="104" aria-hidden="true">
            <circle cx="50" cy="50" r={R} className="dash-ring__track" />
            <circle
              cx="50"
              cy="50"
              r={R}
              className="dash-ring__fill"
              strokeDasharray={`${C * frac} ${C}`}
              transform="rotate(-90 50 50)"
            />
          </svg>
          <div className="dash-ring__center" dir="ltr" data-testid="agenda-center">
            {formatCount(agenda.done)} / {formatCount(agenda.total)}
          </div>
        </div>
        <div className="dash-agenda__pct">
          {t('dashboard.agendaPercent', { percent: Math.round(frac * 100) })}
        </div>
      </div>
      <div className="dash-agenda__readiness">
        <div className="dash-section-label">{t('dashboard.readinessTitle')}</div>
        <ProgressBar
          label={t('dashboard.readinessIsraeli')}
          done={agenda.requiredIsraeliDone}
          total={agenda.requiredIsraeliTotal}
        />
        <ProgressBar
          label={t('dashboard.readinessForeign')}
          done={agenda.requiredForeignDone}
          total={agenda.requiredForeignTotal}
        />
      </div>
    </Card>
  );
}

function AumTile({ data }: { data: DashboardData }) {
  const rows = useMemo(
    () =>
      FUND_TYPE_ORDER.map((id) => data.fundTypes.find((f) => f.fundTypeId === id))
        .filter((f): f is NonNullable<typeof f> => Boolean(f))
        .map((f) => ({ ...f, ...fundTypeViz(f.fundTypeId, f.fundTypeName) })),
    [data.fundTypes],
  );
  const total = rows.reduce((s, r) => s + (r.aum ?? 0), 0);
  const slices = rows.filter((r) => (r.aum ?? 0) > 0);
  const fundsWithAum = rows.reduce((s, r) => s + r.fundsWithAum, 0);
  const hasData = slices.length > 0 && total > 0;
  const asOf = formatDate(data.aumAsOf);

  return (
    <Card title={t('dashboard.aumTitle')} to="/fund-data-history" className="dash-card--hero">
      {!hasData ? (
        <EmptyState />
      ) : (
        <div className="dash-aum">
          <div className="dash-donut" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="aum"
                  nameKey="label"
                  innerRadius="68%"
                  outerRadius="100%"
                  paddingAngle={slices.length > 1 ? 2 : 0}
                  stroke="var(--color-surface)"
                  strokeWidth={slices.length > 1 ? 2 : 0}
                  startAngle={90}
                  endAngle={-270}
                  isAnimationActive={false}
                >
                  {slices.map((s) => (
                    <Cell key={s.fundTypeId} fill={s.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    const p = payload?.[0]?.payload as (typeof slices)[number] | undefined;
                    return (
                      <ChartTooltip
                        active={active}
                        rows={
                          p
                            ? [
                                {
                                  color: p.color,
                                  label: p.label,
                                  value: `${formatShekelsFull(p.aum ?? 0)} (${formatPercent((p.aum ?? 0) / total)})`,
                                },
                              ]
                            : []
                        }
                      />
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="dash-donut__center">
              <div className="dash-donut__total" data-testid="aum-total">
                {formatShekelsShort(total)}
              </div>
              <div className="dash-muted">{t('dashboard.aumTotalLabel')}</div>
            </div>
          </div>
          <ul className="dash-legend">
            {rows.map((r) => (
              <li key={r.fundTypeId} className="dash-legend__row">
                <span className="dash-swatch" style={{ background: r.color }} />
                <span className="dash-legend__name">{r.label}</span>
                <span className="dash-legend__val">
                  {r.aum ? formatShekelsShort(r.aum) : '—'}
                  <span className="dash-legend__pct">{r.aum ? formatPercent(r.aum / total) : ''}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {hasData && (
        <div className="dash-foot dash-muted">
          {asOf && <span>{t('dashboard.asOf', { date: asOf })}</span>}
          <span>{t('dashboard.basedOnFunds', { count: formatCount(fundsWithAum) })}</span>
        </div>
      )}
    </Card>
  );
}

function FundCountTile({ data }: { data: DashboardData }) {
  const { theme } = useTheme();
  const rows = FUND_TYPE_ORDER.map((id) => data.fundTypes.find((f) => f.fundTypeId === id))
    .filter((f): f is NonNullable<typeof f> => Boolean(f))
    .map((f) => ({ ...f, ...fundTypeViz(f.fundTypeId, f.fundTypeName) }));
  const total = rows.reduce((s, r) => s + r.fundCount, 0);
  return (
    <Card
      title={t('dashboard.fundCountTitle')}
      headerExtra={<span className="dash-muted" data-testid="fund-total">{t('dashboard.fundCountTotal', { count: formatCount(total) })}</span>}
      to="/settings/funds"
    >
      <div className="dash-bars" dir="ltr" data-testid="fund-bars">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 56, bottom: 4, left: 4 }} barCategoryGap="28%">
            <XAxis type="number" hide domain={[0, 'dataMax']} />
            <YAxis
              type="category"
              dataKey="label"
              axisLine={false}
              tickLine={false}
              width={96}
              tick={{ fill: 'var(--color-text-primary)', fontSize: 13 }}
            />
            <Tooltip
              cursor={{ fill: 'var(--color-surface-raised)' }}
              content={({ active, payload }) => {
                const p = payload?.[0]?.payload as (typeof rows)[number] | undefined;
                return (
                  <ChartTooltip
                    active={active}
                    rows={p ? [{ color: p.color, label: `${p.label} · ${t('dashboard.fundCountTooltip')}`, value: formatCount(p.fundCount) }] : []}
                  />
                );
              }}
            />
            <Bar dataKey="fundCount" radius={theme === 'mizrahi' ? 8 : 4} maxBarSize={28} minPointSize={2} isAnimationActive={false}>
              {rows.map((r) => (
                <Cell key={r.fundTypeId} fill={r.color} />
              ))}
              <LabelList
                dataKey="fundCount"
                content={(raw) => {
                  const props = raw as unknown as Record<string, unknown>;
                  const x = Number(props.x) || 0;
                  const y = Number(props.y) || 0;
                  const w = Number(props.width) || 0;
                  const h = Number(props.height) || 0;
                  return (
                    <text
                      x={x + w + 8}
                      y={y + h / 2}
                      dominantBaseline="central"
                      textAnchor="start"
                      style={{ fill: 'var(--color-text-primary)', fontSize: 13, fontWeight: 600 }}
                    >
                      {formatCount(Number(props.value))}
                    </text>
                  );
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

// mizrahi only: emphasised last point of the trend line (coral dot + soft halo).
function TrendEndDot(lastIndex: number) {
  return function renderDot(raw: unknown): React.ReactElement<SVGElement> {
    const { cx, cy, index } = raw as { cx?: number; cy?: number; index?: number };
    if (index !== lastIndex || cx == null || cy == null) return <g key={`d${index}`} />;
    return (
      <g key={`d${index}`}>
        <circle cx={cx} cy={cy} r={11} fill="var(--viz-trend)" opacity={0.2} />
        <circle cx={cx} cy={cy} r={5.5} fill="var(--viz-trend)" stroke="var(--color-surface)" strokeWidth={2.5} />
      </g>
    );
  };
}

function TrendTile({ data }: { data: DashboardData }) {
  const { theme } = useTheme();
  const isMizrahi = theme === 'mizrahi';
  const points = useMemo(
    () =>
      data.aumTrend
        .filter((p): p is { dDate: string; aum: number } => p.aum !== null)
        .sort((a, b) => a.dDate.localeCompare(b.dDate)),
    [data.aumTrend],
  );
  return (
    <Card title={t('dashboard.trendTitle')} to="/fund-data-history" className="dash-card--wide">
      {points.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="dash-trend" dir="ltr" data-testid="aum-trend">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={points} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="dash-trend-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--viz-trend)" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="var(--viz-trend)" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="var(--color-border)" strokeDasharray="3 3" />
              <XAxis
                dataKey="dDate"
                tickFormatter={formatDayMonth}
                axisLine={{ stroke: 'var(--color-border-strong)' }}
                tickLine={false}
                tick={{ fill: 'var(--color-text-secondary)', fontSize: 12 }}
                minTickGap={24}
              />
              <YAxis
                tickFormatter={formatShekelsShort}
                axisLine={false}
                tickLine={false}
                width={84}
                tickCount={4}
                tick={{ fill: 'var(--color-text-secondary)', fontSize: 12 }}
                domain={[(min: number) => min * 0.98, (max: number) => max * 1.02]}
              />
              <Tooltip
                cursor={{ stroke: 'var(--color-border-strong)', strokeWidth: 1 }}
                content={({ active, payload }) => {
                  const p = payload?.[0]?.payload as (typeof points)[number] | undefined;
                  return (
                    <ChartTooltip
                      active={active}
                      title={p ? formatDate(p.dDate) ?? undefined : undefined}
                      rows={p ? [{ color: 'var(--viz-trend)', label: t('dashboard.trendSeries'), value: formatShekelsFull(p.aum) }] : []}
                    />
                  );
                }}
              />
              {isMizrahi && (
                <Area
                  type="monotone"
                  dataKey="aum"
                  stroke="none"
                  fill="url(#dash-trend-fill)"
                  isAnimationActive={false}
                  activeDot={false}
                  tooltipType="none"
                />
              )}
              <Line
                type={isMizrahi ? 'monotone' : 'linear'}
                dataKey="aum"
                stroke="var(--viz-trend)"
                strokeWidth={isMizrahi ? 3 : 2}
                dot={isMizrahi ? TrendEndDot(points.length - 1) : points.length < 20}
                activeDot={{ r: 4, stroke: 'var(--color-surface)', strokeWidth: 2 }}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

function Skeleton() {
  return (
    <div className="dash-grid" aria-busy="true" data-testid="dash-skeleton">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className={`dash-card dash-skel dash-skel--${i}`}>
          <div className="dash-skel__bar dash-skel__bar--title" />
          <div className="dash-skel__bar dash-skel__bar--hero" />
          <div className="dash-skel__bar" />
          <div className="dash-skel__bar dash-skel__bar--short" />
        </div>
      ))}
    </div>
  );
}

// ───── page ─────

export function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDashboard();
      if (!mounted.current) return;
      setData(res);
      setError(null);
      setUpdatedAt(new Date());
    } catch (e) {
      if (!mounted.current) return;
      showErrorToast(e, { title: t('errorToast.titleLoad') });
      setError(describeError(e).body);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    const id = window.setInterval(load, REFRESH_MS);
    return () => {
      mounted.current = false;
      window.clearInterval(id);
    };
  }, [load]);

  return (
    <div className="dash">
      <div className="dash__bar">
        <h1 className="dash__title">{t('dashboard.pageTitle')}</h1>
        <div className="dash__bar-end">
          {updatedAt && (
            <span className="dash-muted" data-testid="dash-updated">
              {t('dashboard.updatedAt', { time: formatClock(updatedAt) })}
            </span>
          )}
          <LopButton
            iconOnly
            className="dash__refresh"
            onClick={load}
            loading={loading}
            icon={<RefreshCw size={16} aria-hidden="true" />}
            aria-label={t('dashboard.refresh')}
            title={t('dashboard.refresh')}
          />
        </div>
      </div>

      {error && (
        <div className="dash__error" role="alert" data-testid="dash-error">
          {t('dashboard.loadError', { message: error })}
        </div>
      )}

      {!data && loading && <Skeleton />}

      {data && (
        <div className="dash-grid" data-testid="dash-grid">
          <ValuationTile data={data} />
          <AlertsTile data={data} />
          <AgendaTile data={data} />
          <AumTile data={data} />
          <FundCountTile data={data} />
          <TrendTile data={data} />
        </div>
      )}
    </div>
  );
}
